import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Plus, MapPin, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, usePrefs } from "@/lib/context";
import { fetcher, useCategories } from "@/lib/hooks";
import { PageHeader, Empty, AuthImage } from "@/components/common";
import { PropertyDialog } from "@/components/PropertyDialog";

const FALLBACK = "https://images.unsplash.com/photo-1719258833451-1e47c1498467?w=900&q=80";

function PropertyCard({ p, i }) {
  const { t } = usePrefs();
  const { label } = useCategories();
  const rate = p.units_count ? Math.round((100 * p.occupied_count) / p.units_count) : 0;
  return (
    <Link to={`/properties/${p.id}`} data-testid={`property-card-${p.id}`} className={`rise rise-${(i % 4) + 1} card-lift group block overflow-hidden rounded-sm border border-slate-200 bg-white`}>
      <div className="relative h-44 overflow-hidden">
        <AuthImage path={p.photos?.[0] || FALLBACK} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <span className="absolute left-3 top-3 rounded-sm bg-white/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest backdrop-blur">{label(p.type)}</span>
        <span className="absolute right-3 top-3 rounded-sm bg-slate-900/80 px-2 py-0.5 font-data text-[10px] text-white">{p.reference}</span>
      </div>
      <div className="p-5">
        <h3 className="font-display text-xl font-bold text-slate-900">{p.name}</h3>
        <div className="mt-1 flex items-center gap-1 text-xs text-slate-500"><MapPin size={12} />{p.address}, {p.city}</div>
        <div className="mt-4 grid grid-cols-3 gap-2 border-t border-slate-100 pt-4 text-center">
          <div><div className="font-data text-lg">{p.units_count}</div><div className="text-[10px] uppercase tracking-wider text-slate-500">{t("units")}</div></div>
          <div><div className="font-data text-lg">{rate}%</div><div className="text-[10px] uppercase tracking-wider text-slate-500">{t("occupe")}</div></div>
          <div><div className={`flex items-center justify-center gap-1 font-data text-lg ${p.open_tickets ? "text-amber-700" : ""}`}><Wrench size={13} />{p.open_tickets}</div><div className="text-[10px] uppercase tracking-wider text-slate-500">{t("tickets")}</div></div>
        </div>
      </div>
    </Link>
  );
}

export default function Properties() {
  const { user } = useAuth();
  const { t } = usePrefs();
  const { data = [], isLoading } = useQuery({ queryKey: ["properties"], queryFn: () => fetcher("/properties"), refetchInterval: 30000 });
  const staff = ["admin", "supervision", "gestionnaire"].includes(user.role);
  return (
    <div data-testid="properties-page">
      <PageHeader eyebrow={t("portfolio")} title={t("properties")}>
        {staff && <PropertyDialog trigger={<Button className="rounded-sm bg-[#0F172A] hover:bg-[#B8860B]" data-testid="new-property-button"><Plus size={16} className="mr-1" />{t("newProperty")}</Button>} />}
      </PageHeader>
      {!isLoading && data.length === 0 && <Empty />}
      <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">{data.map((p, i) => <PropertyCard key={p.id} p={p} i={i} />)}</div>
    </div>
  );
}
